/**
 * Chant-document hooks — the editor's data layer.
 *
 * Chunked to match the API (05 §3.2): the head (manifest + section index) is
 * one query, and each section is its own query, so opening a 1.73 MB document
 * fetches a few KB and the sections arrive as they are needed.
 *
 * Every request goes through `apiFetch`, never a bare relative path: in the
 * packaged app a bare `/api/...` resolves to the WebView's own origin, not the
 * API.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ChantDocHead,
  ChantDocInput,
  ChantDocPatch,
  ChantDocSummary,
  ChantSection,
  ChantSectionIndexEntry,
} from '@vedaunion/shared';
import { ApiError, apiFetch, apiRequest } from './api';

const KEY = ['chants'] as const;

export interface ChantSectionPayload {
  sectionId: string;
  sortOrder: number;
  hash: string;
  bytes: number;
  section: ChantSection;
  updatedAt: string;
}

/** Every document the signed-in user may see. */
export function useChants() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const r = await apiFetch<{ chants: ChantDocSummary[] }>('/api/chants');
      return r.chants;
    },
  });
}

/** The head: manifest + section index, without any section's text. */
export function useChantHead(slug: string | undefined) {
  return useQuery({
    queryKey: [...KEY, slug, 'head'],
    enabled: typeof slug === 'string' && slug.length > 0,
    queryFn: async () => {
      const r = await apiFetch<{ chant: ChantDocHead }>(`/api/chants/${slug}`);
      return r.chant;
    },
  });
}

/** One section. Cached per section, so switching back is instant. */
export function useChantSection(slug: string | undefined, sectionId: string | undefined) {
  return useQuery({
    queryKey: [...KEY, slug, 'section', sectionId],
    enabled:
      typeof slug === 'string' && slug.length > 0
      && typeof sectionId === 'string' && sectionId.length > 0,
    queryFn: async () => {
      const r = await apiFetch<{ section: ChantSectionPayload }>(
        `/api/chants/${slug}/sections/${sectionId}`,
      );
      return r.section;
    },
  });
}

export function useCreateChant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ChantDocInput) => {
      const r = await apiFetch<{ chant: ChantDocSummary }>('/api/chants', {
        method: 'POST',
        body: input,
      });
      return r.chant;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function usePatchChant(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: ChantDocPatch) => {
      const r = await apiFetch<{ chant: ChantDocSummary }>(`/api/chants/${slug}`, {
        method: 'PATCH',
        body: patch,
      });
      return r.chant;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...KEY, slug, 'head'] });
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** A conflict carries the CURRENT section, so the editor can show a diff. */
export interface SectionConflict {
  message: string;
  current: ChantSectionPayload;
}

/**
 * Read a save failure as a conflict, or `null` when it was something else.
 *
 * `ApiError` keeps the parsed body, so the other side's version survives the
 * throw and the author can be shown what changed instead of losing their work.
 */
export function asSectionConflict(err: unknown): SectionConflict | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as { message?: string; current?: ChantSectionPayload } | undefined;
  if (body?.current === undefined) return null;
  return { message: body.message ?? 'This section changed since you loaded it.', current: body.current };
}

/**
 * Save one section, guarded by the hash it was loaded at.
 *
 * A 409 is not an error to swallow: it means someone else changed this section,
 * and the response carries their version so the author can compare. Never a
 * silent overwrite.
 */
export function useSaveSection(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      section: ChantSection;
      baseHash?: string;
      sortOrder?: number;
    }) => {
      const r = await apiFetch<{
        hash: string;
        bytes: number;
        sections: ChantSectionIndexEntry[];
      }>(`/api/chants/${slug}/sections/${args.section.id}`, {
        method: 'PUT',
        body: {
          section: args.section,
          ...(args.baseHash !== undefined ? { baseHash: args.baseHash } : {}),
          ...(args.sortOrder !== undefined ? { sortOrder: args.sortOrder } : {}),
        },
      });
      return r;
    },
    onSuccess: (_r, args) => {
      void qc.invalidateQueries({ queryKey: [...KEY, slug, 'head'] });
      void qc.invalidateQueries({ queryKey: [...KEY, slug, 'section', args.section.id] });
    },
  });
}

export function useReorderSections(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sectionIds: string[]) =>
      apiFetch<{ sections: ChantSectionIndexEntry[] }>(
        `/api/chants/${slug}/sections/reorder`,
        { method: 'POST', body: { sectionIds } },
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...KEY, slug, 'head'] }),
  });
}

export function useDeleteSection(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sectionId: string) =>
      apiFetch<{ sections: ChantSectionIndexEntry[] }>(
        `/api/chants/${slug}/sections/${sectionId}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...KEY, slug, 'head'] }),
  });
}

export function usePublishChant(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      apiFetch<{ published: boolean; bytes: number; url: string }>(
        `/api/chants/${slug}/publish`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...KEY, slug, 'head'] });
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useSnapshotChant(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (note?: string) =>
      apiFetch<{ version: number }>(`/api/chants/${slug}/revisions`, {
        method: 'POST',
        body: note === undefined ? {} : { note },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...KEY, slug, 'head'] }),
  });
}

/** The eleven shipped chants, offered as a starting point for a new document. */
export const SHIPPED_CHANTS: readonly { slug: string; title: string }[] = [
  { slug: 'purusha-suktam', title: 'Puruṣa Sūktam' },
  { slug: 'durga-suktam', title: 'Durgā Sūktam' },
  { slug: 'bhagya-suktam', title: 'Bhāgya Sūktam' },
  { slug: 'vishnu-suktam', title: 'Viṣṇu Sūktam' },
  { slug: 'mantra-pushpam', title: 'Mantra Puṣpam' },
  { slug: 'sri-rudram', title: 'Śrī Rudram' },
  { slug: 'shiva-sankalpa-suktam', title: 'Śiva Saṅkalpa Sūktam' },
  { slug: 'ganapati-atharvashirsham', title: 'Gaṇapati Atharvaśīrṣa' },
  { slug: 'ganesha-ashtottara', title: 'Gaṇeśa Aṣṭottara' },
  { slug: 'lakshmi-ashtottara', title: 'Lakṣmī Aṣṭottara' },
  { slug: 'puja-vidhi', title: 'Pūjā Vidhi' },
];

/**
 * Import a marked `.docx`.
 *
 * `multipart`, so it does not go through `apiFetch` (which JSON-encodes). The
 * report comes back with the document and must be shown: an import that
 * normalised something silently would be exactly the wrong thing.
 */
export interface DocxImportReport {
  structure: { paragraphs: number; runs: number; sections: number; verses: number; syllables: number };
  marks: Record<string, number>;
  byStyle: Record<string, number>;
  unresolved: { at: string; what: string; raw: string }[];
}

export function useImportDocx() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { file: File; title?: string }) => {
      const body = new FormData();
      body.set('file', args.file);
      if (args.title !== undefined) body.set('title', args.title);
      // `apiRequest`, not a hand-rolled fetch. It resolves the API
      // origin and attaches THIS build's credential — a cookie on the
      // web, a bearer header in a packaged shell. Hand-rolling it here
      // hardcoded `credentials: 'include'`, which on the cross-origin
      // request a shell makes is rejected by the browser before it
      // leaves, as a bare "Failed to fetch" with nothing to debug.
      // Found by opening a .vuchant in the desktop app.
      const res = await apiRequest('/api/chants/import/docx', { method: 'POST', body });
      const json = (await res.json().catch(() => ({}))) as {
        chant?: ChantDocSummary; report?: DocxImportReport; message?: string; error?: string;
      };
      if (!res.ok || json.chant === undefined) {
        throw new ApiError(res.status, json.message ?? json.error ?? 'Import failed.', undefined);
      }
      return { chant: json.chant, report: json.report };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Download an export and put it wherever the user is.
 *
 * A plain `<a download>` is right on the web and DOES NOT WORK in a packaged
 * shell: the WebView has no download manager, so the link either opens the
 * bytes in place or does nothing at all. `saveBytes` knows both answers — a
 * Save dialog and a real file on the desktop, the browser's own download on
 * the web — so a caller does not have to know which it is in.
 *
 * Returns where it went, or `null` if the user cancelled the Save dialog.
 */
export async function downloadExport(
  slug: string,
  kind: 'docx' | 'vuchant',
): Promise<string | null> {
  const res = await apiRequest(
    kind === 'docx' ? `/api/chants/${slug}/export/docx` : `/api/chants/${slug}/package`,
  );
  if (!res.ok) {
    throw new ApiError(res.status, `Could not export this chant (${res.status}).`, undefined);
  }
  const bytes = new Uint8Array(await res.arrayBuffer());
  const { saveBytes } = await import('./desktop');
  return saveBytes(
    `${slug}.${kind}`,
    bytes,
    kind === 'docx'
      ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      : 'application/vnd.vedaunion.chant+zip',
    kind === 'docx'
      ? [{ name: 'Word document', extensions: ['docx'] }]
      : [{ name: 'Chant package', extensions: ['vuchant'] }],
  );
}

/** Upload a `.vuchant`. Refused, not repaired, if its document has been edited
 *  outside the tools — the server checks the manifest hash. */
export function useImportPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.set('file', file);
      // `apiRequest`, not a hand-rolled fetch. It resolves the API
      // origin and attaches THIS build's credential — a cookie on the
      // web, a bearer header in a packaged shell. Hand-rolling it here
      // hardcoded `credentials: 'include'`, which on the cross-origin
      // request a shell makes is rejected by the browser before it
      // leaves, as a bare "Failed to fetch" with nothing to debug.
      // Found by opening a .vuchant in the desktop app.
      const res = await apiRequest('/api/chants/import/package', { method: 'POST', body });
      const json = (await res.json().catch(() => ({}))) as {
        chant?: ChantDocSummary;
        manifest?: { title: string; engine: string; createdAt: string; docHash: string };
        assets?: number;
        message?: string;
        error?: string;
      };
      if (!res.ok || json.chant === undefined) {
        throw new ApiError(res.status, json.message ?? json.error ?? 'Import failed.', undefined);
      }
      return { chant: json.chant, manifest: json.manifest, assets: json.assets ?? 0 };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

// =============================================================================
// Sharing — one document, specific people
//
// A share grants SCOPE, never CAPABILITY: sharing a document as `editor` lets
// that person edit THIS document, and does not give them `chants.create` or
// the right to touch anything else. The server enforces it
// (`lib/chant-access.ts`); this layer only has to avoid implying otherwise.
// =============================================================================

export interface ChantShare {
  userId: number;
  role: 'viewer' | 'editor';
  name: string | null;
  email: string;
  createdAt: string;
}

/** What the signed-in user may do with this document, as the server sees it. */
export interface ChantAbilities {
  read: boolean;
  write: boolean;
  publish: boolean;
  share: boolean;
  delete: boolean;
}

export function useChantShares(slug: string | undefined) {
  return useQuery({
    queryKey: [...KEY, slug, 'shares'],
    enabled: typeof slug === 'string' && slug.length > 0,
    queryFn: async () =>
      apiFetch<{ shares: ChantShare[]; abilities: ChantAbilities }>(
        `/api/chants/${slug}/shares`,
      ),
  });
}

export function useShareChant(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { email: string; role: 'viewer' | 'editor' }) =>
      apiFetch<{ shared: boolean; userId: number; role: string }>(
        `/api/chants/${slug}/shares`,
        { method: 'PUT', body: args },
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...KEY, slug, 'shares'] }),
  });
}

export function useUnshareChant(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) =>
      apiFetch<{ removed: boolean }>(`/api/chants/${slug}/shares/${userId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...KEY, slug, 'shares'] }),
  });
}

/**
 * Every chant that may be EMBEDDED in a Library document.
 *
 * Two sources, one list, because a document author does not care where a chant
 * lives — only whether they may point at it:
 *
 *   - the eleven shipped chants, which every reader can already fetch;
 *   - the author's own documents and those shared with them, once published.
 *
 * An unpublished chant is deliberately absent. Embedding one would put a draft
 * in front of every reader of the Library page, and the end user is only ever
 * meant to see what a document points at.
 */
export interface EmbeddableChant {
  slug: string;
  title: string;
  origin: 'shipped' | 'mine';
  subtitle?: string | null;
  sections?: number;
  verses?: number;
}

export function useEmbeddableChants() {
  const mine = useChants();
  const rows: EmbeddableChant[] = [
    ...SHIPPED_CHANTS.map((c) => ({ ...c, origin: 'shipped' as const })),
    ...(mine.data ?? [])
      .filter((c) => c.status === 'published')
      // A published copy of a shipped chant would otherwise appear twice.
      .filter((c) => !SHIPPED_CHANTS.some((s) => s.slug === c.slug))
      .map((c) => ({
        slug: c.slug,
        title: c.title,
        origin: 'mine' as const,
        subtitle: c.subtitle,
        sections: c.sectionCount,
        verses: c.verses,
      })),
  ];
  return { ...mine, chants: rows };
}
